import EventEmitter from 'event-emitter';
import Adapter from './message/adapter';
import { KV } from './common/constant';
import { checkDevice } from './utils/utils';
import Store from './common/store';
import API from './message/api';
import { generateKey, encodeRSA, decodeAES, setSecretKey, setSignType } from './shared/aut';
import pkg from '../package.json';

export default class Interface {
  constructor(VhallMsg, params) {
    EventEmitter(this);
    this.VhallMsg = VhallMsg;
    this.opt = params || {};
    this.compChannelId = undefined; // 新增压缩频道，可能没有
    this.adapter = null;
    this.store = new Store();
    this.api = new API(this.store);
    this.onOnline = null;
    this.onOffline = null;
  }

  async init() {
    const client = this.opt.client || checkDevice();
    this.store.setKV(KV.APP_ID, this.opt.appId);
    this.store.setKV(KV.ROOM_ID, this.opt.roomId || '');
    this.store.setKV(KV.THIRD_PARTY_USER_ID, this.opt.third_party_user_id);
    this.store.setKV(KV.ACCESS_TOKEN, this.opt.token);
    this.store.setKV(KV.PACKAGE_CHECK, 'package_check');
    this.store.setKV(KV.CLIENT, client);
    this.store.setKV(KV.API_SERVER, this.opt.api_server);

    const rnd = generateKey(48); // 生成随机字符串
    // const rnd = '3f77Ew8jkv0H1U6BnXoGJxRxBMf7K44A2vuN7v81GLkWqtAK'; // TODO 测试数据，待删除
    const random_key = encodeRSA(rnd, process.env.PUBLIC_KEY); // RSA加密
    // 注意: 如果公钥不合法，random_key得到的值是false
    // API请求
    this.info = await this.api.init({
      channel_id: this.opt.channelId,
      random_key,
      version: `jssdk_msg_${pkg.version}`
    });

    if (this.info.comp_channel_id) {
      // 如果存在压缩频道，压缩频道就是建立连接的频道
      this.compChannelId = this.info.comp_channel_id;
    }
    if (this.info.secret_key) {
      // AES解密
      const secretKey = decodeAES(this.info.secret_key, rnd.substring(0, 32), rnd.substring(32, 48));
      // log.debug('secretKey:', secretKey);
      setSignType(this.info.sign_type); // 存储签名类型，0-MD5 2-SHA256 3-SM3，默认2
      setSecretKey(secretKey); // 存储秘钥
    }
  }

  async connect(callback) {
    // Adapter 复用
    if (this.adapter) {
      this.adapter.join(this.compChannelId || this.opt.channelId, this.opt, callback);
      return this;
    }
    const adapterOptions = {
      channelId: this.compChannelId || this.opt.channelId,
      third_party_user_id: this.opt.third_party_user_id,
      socket_server: this.info.socket_server,
      connection_token: this.info.connection_token,
      nginx_server: this.info.nginx_server,
      context: this.opt.context,
      hide: this.opt.hide,
      extra_connection_info: this.info?.extra_connection_info
    };
    this.adapter = new Adapter(adapterOptions, this);
    this.adapter.on('online', () => this.onOnline && this.onOnline());
    this.adapter.on('offline', () => this.onOffline && this.onOffline());
    this.adapter.on('disconnect', reason => this.onDisconnect && this.onDisconnect(reason));
    this.adapter.on('reconnectAttempt', attempt => this.onReconnectAttempt && this.onReconnectAttempt(attempt));
    this.adapter.on('service_message', data => {
      // 不存 service_type 的是旧消息格式，不处理
      if (!data.service_type) return;
      // 与当前频道不一致的，不处理
      if (data.channel && !(adapterOptions.channelId || '').includes(data.channel)) return;

      try {
        // service_type目前支持：service_im、service_custom、service_document、service_room、service_online 五种。
        this.emit(data.service_type, data);
      } catch (e) {
        /**/
      }
    });

    // 初始化连接
    const joinMsg = await this.adapter.connect();
    callback && callback(joinMsg);
    return joinMsg;
  }

  /**
   * 彻底销毁，及其连接复用
   */
  disconnect() {
    this.destroy();
    this.adapter && this.adapter.disconnect();
    this.VhallMsg.channlIdMap = {};
  }

  /**
   * 销毁实例（注意连接复用问题）
   */
  destroy() {
    if (this.adapter) {
      this.adapter.leave(this.compChannelId || this.opt.channelId);
    }
    if (this.adapter && !this.adapter.hasChannelCount()) {
      this.adapter.disconnect();
    }
    // 修复多个sdk连接复用问题
    this.VhallMsg.channlIdMap[this.opt.channelId] = null;
  }

  // 销毁tim实例
  destroyTim() {
    return this.adapter?._tim?.destroy();
  }

  // 退出群组
  quitTimGroups() {
    return this.adapter?._tim?.quitGroups();
  }

  /**
   * 退出 channel
   */
  leave() {
    this.adapter.leave(this.compChannelId || this.opt.channelId);
  }

  // 获取tim登录状态
  getTimLoginStatus() {
    return this.adapter?._tim?.getLoginStatus();
  }

  /**
   * 消息监听
   * @param {string} event 事件名
   * @param {function} handler 处理函数
   */
  addEventListener(event, handler) {
    this.on(event, handler);
  }

  removeEventListener(event, handler) {
    this.off(event, handler);
  }

  // 更新了opt并没有更新store, 数据不会变，为什么有此处理？
  updateOption(opt) {
    this.opt = opt;
  }

  maybeCheckWs(force) {
    if (this.adapter) this.adapter.maybeCheckWs(force);
  }

  /**
   * 消息派发
   * @param params
   * @param success
   * @param failure
   */
  async emitEvent(params, success, failure) {
    if (!this.adapter.online) {
      return null;
    }
    const { accountId, type, msg, context, user_ids, no_audit = 0, tag } = params;
    accountId && this.store.setKV(KV.THIRD_PARTY_USER_ID, accountId);
    const isDirect = typeof user_ids === 'string' && user_ids;
    let res = null;
    try {
      if (isDirect) {
        res = await this.api.sendDirect(type, this.opt.channelId, user_ids, msg, context, no_audit, tag);
      } else {
        res = await this.api.sendMsg(type, this.opt.channelId, msg, context, no_audit, tag);
      }
    } catch (error) {
      const e = {
        code: error.code,
        data: error.data,
        message: error.msg
      };
      failure && failure(e);
      throw e;
    }
    success && success(res);
    return res;
  }
}
